/**
 * NF PARCIAL: quais itens da fatura já estão numa NFS-e ativa.
 *
 * ⛔ O modal abria com TODOS os itens marcados, sem olhar as notas que já
 * existiam. Na FAT202607014 (FUNDETEC) o STFC de R$ 1.800,00 já estava na
 * nota 9953, Autorizada e paga; o modal abria marcando os três itens
 * (R$ 3.200,00) e o backend recusava ao criar:
 *   "Soma das NFS-e ativas (R$ 1800.00) + esta parcial (R$ 3200.00)
 *    ultrapassa o total da fatura (R$ 3200.00)".
 * Agora o item coberto abre desmarcado e travado, dizendo em qual nota está.
 *
 * "ATIVA" É A MESMA REGRA DO BACKEND (`fiscal-document.service.ts`,
 * createPartialNfse): NFS-e com status diferente de Cancelada. Rascunho,
 * Processando, Rejeitada e Erro também contam. Se a tela usasse outra regra,
 * voltaria a oferecer um estado que o backend recusa.
 *
 * ⚠️ O NOME NA NOTA TEM O SUFIXO DO EMPENHO ("… — Nota de Empenho nº
 * 232/2026") e o da fatura não. A comparação tira o sufixo com
 * `SUFIXO_DE_EMPENHO`, a MESMA expressão que o modal usa para trocar o número.
 */

/** Sufixo que o modal acrescenta ao nome do item para o empenho sair na NFS-e. */
export const SUFIXO_DE_EMPENHO = /\s*—\s*Nota de Empenho nº .*$/i;

export const nomeSemEmpenho = (nome: unknown): string =>
  String(nome ?? '').replace(SUFIXO_DE_EMPENHO, '').trim();

export interface NotaDoItem {
  id: number;
  documentNumber: string | null;
  status: string;
}

export interface CoberturaDaNf {
  /** Mesma ordem dos itens da fatura; null = item livre. */
  porItem: Array<NotaDoItem | null>;
  /** Soma das NFS-e ativas (o que o backend soma para recusar). */
  jaNotado: number;
  /** Total da fatura menos o já notado, nunca negativo. */
  resta: number;
}

const ativa = (d: any) => String(d?.type || '').toLowerCase() === 'nfse' && d?.status !== 'Cancelada';
const r2 = (v: number) => Math.round(v * 100) / 100;

export function coberturaDaNf(itensDaFatura: any[], notas: any[], totalDaFatura: number): CoberturaDaNf {
  const itens = Array.isArray(itensDaFatura) ? itensDaFatura : [];
  const ativas = (Array.isArray(notas) ? notas : []).filter(ativa);

  // Cada item de nota cobre UM item de fatura: duas linhas iguais na fatura só
  // ficam as duas cobertas se a nota também tiver as duas.
  const livres: Array<{ nome: string; productId: number | null; nota: NotaDoItem }> = [];
  for (const d of ativas) {
    const nota: NotaDoItem = { id: d.id, documentNumber: d.documentNumber ?? null, status: d.status };
    for (const it of Array.isArray(d.items) ? d.items : []) {
      livres.push({ nome: nomeSemEmpenho(it?.name), productId: Number(it?.productId) || null, nota });
    }
  }

  const porItem = itens.map((it) => {
    const nome = nomeSemEmpenho(it?.name);
    const pid = Number(it?.productId) || null;
    const i = livres.findIndex(
      (l) => l.nome === nome && (l.productId == null || pid == null || l.productId === pid),
    );
    if (i < 0) return null;
    const [achado] = livres.splice(i, 1);
    return achado.nota;
  });

  const jaNotado = r2(ativas.reduce((s, d) => s + (Number(d.totalValue) || 0), 0));
  return { porItem, jaNotado, resta: Math.max(0, r2((Number(totalDaFatura) || 0) - jaNotado)) };
}

/**
 * GRUPOS POR EMPENHO: uma NFS-e por empenho, sem marcar item a item.
 *
 * Desde o backend #273 o item da FATURA tem `empenhoNumber` como campo,
 * copiado do item do contrato. Na FUNDETEC (contrato 180) a fatura sai com a
 * locação no 328/2026 e o STFC e a instalação sem empenho no item: dois grupos,
 * duas notas.
 *
 * ⛔ SÓ O CAMPO. Fatura anterior ao #273 não tem o campo nem o sufixo no nome
 * (o sufixo só existia no item da NOTA, criado pelo modal); parsear o nome não
 * recuperaria nada e só abriria uma forma de errar. Sem o campo, todos os itens
 * caem no grupo "sem empenho" e o modal funciona como sempre funcionou.
 *
 * O grupo "sem empenho no item" usa o empenho da FATURA (`inv.empenhoNumber`,
 * o "Nº do Empenho desta fatura"), que é onde entra o empenho do serviço, que
 * muda todo mês.
 *
 * Um grupo com todos os itens já em NFS-e ativa está CONCLUÍDO: não é
 * oferecido para criar (a regra do #178 continua valendo).
 */
export interface GrupoDeEmpenho {
  /** Empenho do item ('' = sem empenho no item). */
  chave: string;
  /** Empenho que vai para a nota: o do item, ou o da fatura no grupo sem. */
  empenho: string;
  indices: number[];
  /** Itens do grupo ainda sem NFS-e ativa. */
  livres: number[];
  /** Soma dos itens livres. */
  totalLivre: number;
  concluido: boolean;
}

/**
 * O título e a linha de detalhe de um grupo de empenho no modal "NF parcial". TRÊS situações, não duas:
 *   - item com empenho próprio            → o empenho dele;
 *   - item sem empenho, fatura com empenho → HERANÇA: a nota sai com o empenho da fatura. Não é falta, e o
 *     título diz o número herdado (a NFS-e 10420 da FUNDETEC saiu assim, com o 347/2026 da fatura);
 *   - item sem empenho, fatura sem empenho → FALTA de verdade, e só ela vem em aviso.
 * Até 06/10/2026 os dois últimos dividiam o título "Sem empenho no item" e a herança parecia problema.
 * ⛔ Não "consertar" exigindo empenho em todo item: a herança é necessária (fatura com dois empenhos).
 */
export function rotuloDoGrupoDeEmpenho(g: Pick<GrupoDeEmpenho, 'chave' | 'empenho'>): { titulo: string; detalhe: string; aviso?: boolean } {
  if (g.chave) return { titulo: `Empenho ${g.chave}`, detalhe: '' };
  if (g.empenho) return { titulo: `Herda o empenho da fatura (${g.empenho})`, detalhe: 'a nota sai com ele' };
  return { titulo: 'Sem empenho: nem no item, nem na fatura', detalhe: 'a fatura não tem empenho registrado', aviso: true };
}

export function gruposPorEmpenho(
  itens: any[],
  porItem: Array<NotaDoItem | null>,
  empenhoDaFatura: string | null | undefined,
): GrupoDeEmpenho[] {
  const daFatura = String(empenhoDaFatura ?? '').trim();
  const mapa = new Map<string, GrupoDeEmpenho>();
  (Array.isArray(itens) ? itens : []).forEach((it, i) => {
    const chave = String(it?.empenhoNumber ?? '').trim();
    let g = mapa.get(chave);
    if (!g) {
      g = { chave, empenho: chave || daFatura, indices: [], livres: [], totalLivre: 0, concluido: false };
      mapa.set(chave, g);
    }
    g.indices.push(i);
    if (!porItem[i]) {
      g.livres.push(i);
      g.totalLivre = r2(g.totalLivre + (Number(it?.totalValue) || 0));
    }
  });
  const grupos = Array.from(mapa.values());
  grupos.forEach((g) => { g.concluido = g.livres.length === 0; });
  // Com empenho primeiro, em ordem; o grupo sem empenho no item por último.
  return grupos.sort((a, b) => (a.chave === '' ? 1 : b.chave === '' ? -1 : a.chave.localeCompare(b.chave)));
}
